import { create } from "zustand";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { getKeyFromStorage as getStoredKey, clearKeyFromStorage as clearStoredKey, clearSeed } from "../utils/crypto";
import { isCryptoEnvironmentError } from "../utils/cryptoErrors";
import { captureException } from "../observability/sentry";

// Legacy (global) keys — kept for migration from unscoped to user-scoped storage
const LEGACY_E2EE_COMPLETE_KEY = "@cynosure/e2ee_setup_complete";
const LEGACY_IS_NEW_USER_KEY = "@cynosure/is_new_user";
// Legacy. Nothing writes this any more — the flag it backed was set on
// email-code login, and that path retired with Cynosure's own auth in Phase 4.
// The key is still REMOVED on logout and forget-device so an install that
// upgraded across the cutover does not keep a stale value on disk forever.
const LEGACY_PASSKEY_ENROLLMENT_KEY = "@cynosure/passkey_enrollment_required";
const USER_KEY = "@cynosure/user";

/** User-scoped key to prevent stale state from a different user's session. */
const e2eeCompleteKey = (userId: string) => `@cynosure/e2ee_setup_complete:${userId}`;
const isNewUserKey = (userId: string) => `@cynosure/is_new_user:${userId}`;
const personalTeamIdKey = (userId: string) => `@cynosure/personal_team_id:${userId}`;

interface KeyProbe {
  hasKey: boolean;
  /** Set when storage itself failed — see ./utils/cryptoErrors. */
  error: string | null;
}

const NO_KEY: KeyProbe = { hasKey: false, error: null };

/**
 * Whether a device key exists for `userId`, keeping "absent" apart from
 * "storage is broken". Both leave `hasLocalKey` false and send the user to the
 * restore screen; only the second carries a message, and that message is the
 * difference between "enter your 24 words" and "this browser cannot hold a key".
 * The old code collapsed them, and Safari users were shown a blank recovery
 * form for a key that was still on the device.
 */
async function probeStoredKey(userId: string): Promise<KeyProbe> {
  try {
    return { hasKey: (await getStoredKey(userId)) != null, error: null };
  } catch (error) {
    if (isCryptoEnvironmentError(error)) return { hasKey: false, error: error.message };
    throw error;
  }
}

interface EncryptionState {
  /** Whether E2EE setup is complete (mirrors server state) */
  e2eeSetupComplete: boolean;
  /** Transient: recovery phrase words during setup only */
  recoveryPhrase: string[] | null;
  /** Whether the user is new (first time setup vs restore) */
  isNewUser: boolean;
  /** Whether a local encryption key exists on this device */
  hasLocalKey: boolean;
  /**
   * Why `hasLocalKey` is false when it is false because storage FAILED rather
   * than because nothing is stored. Null in every other state. The restore
   * screen shows it, so the user is told the real reason instead of being
   * asked for a recovery phrase.
   */
  keyStorageError: string | null;
  /** Whether the store has been initialized from storage */
  isInitialized: boolean;
  /** The current user ID for scoping the encryption key */
  currentUserId: string | null;
  /** The user's personal team ID (persisted per user) */
  personalTeamId: string | null;
  /** In-memory only: teamId -> AES key for team encryption */
  teamKeys: Record<string, unknown>;
}

interface EncryptionActions {
  initialize: () => Promise<void>;
  reinitializeForUser: (userId: string) => Promise<void>;
  setE2EEComplete: (complete: boolean) => void;
  setRecoveryPhrase: (phrase: string[]) => void;
  setIsNewUser: (isNew: boolean) => void;
  setHasLocalKey: (has: boolean) => void;
  /** Clear sensitive data from memory (phrase and indices) */
  clearSensitiveData: () => void;
  /** Soft logout: clear in-memory state only, preserve key on device */
  clearSession: () => void;
  /**
   * Hard logout: delete the encryption key and seed from device storage.
   * `fallbackUserId` is used when `currentUserId` is already null — which it
   * is after any `clearSession` — because there is no unscoped slot to fall
   * back to, and "forget this device" must never report success having
   * deleted nothing.
   */
  forgetDevice: (fallbackUserId?: string) => Promise<void>;
  /** Set the user's personal team ID */
  setPersonalTeamId: (teamId: string) => void;
  /** Store a team AES key in memory */
  setTeamKey: (teamId: string, key: unknown) => void;
  /** Retrieve a team AES key from memory */
  getTeamKey: (teamId: string) => unknown | null;
  /** Clear all in-memory team keys */
  clearTeamKeys: () => void;
}

export const useEncryptionStore = create<EncryptionState & EncryptionActions>(
  (set, get) => ({
    e2eeSetupComplete: false,
    recoveryPhrase: null,
    isNewUser: false,
    hasLocalKey: false,
    keyStorageError: null,
    isInitialized: false,
    currentUserId: null,
    personalTeamId: null,
    teamKeys: {},

    initialize: async () => {
      try {
        // Read the last-known user ID from AsyncStorage
        const userJson = await AsyncStorage.getItem(USER_KEY);
        let userId: string | null = null;
        if (userJson) {
          try {
            const user = JSON.parse(userJson);
            userId = user.id || null;
          } catch {
            // Invalid JSON, ignore
          }
        }

        // Read user-scoped keys (with legacy fallback + migration)
        const keysToRead = userId
          ? [e2eeCompleteKey(userId), isNewUserKey(userId)]
          : [LEGACY_E2EE_COMPLETE_KEY, LEGACY_IS_NEW_USER_KEY];

        const [results, keyProbe] = await Promise.all([
          AsyncStorage.multiGet(keysToRead),
          userId ? probeStoredKey(userId) : Promise.resolve(NO_KEY),
        ]);

        let e2eeComplete = results[0][1] === "true";
        let isNew = results[1][1] === "true";

        // Migrate legacy global keys → user-scoped keys
        if (userId && !results[0][1] && !results[1][1]) {
          const [legacyE2ee, legacyNew] = await AsyncStorage.multiGet([
            LEGACY_E2EE_COMPLETE_KEY,
            LEGACY_IS_NEW_USER_KEY,
          ]);
          if (legacyE2ee[1] || legacyNew[1]) {
            e2eeComplete = legacyE2ee[1] === "true";
            isNew = legacyNew[1] === "true";
            // Write scoped keys and remove legacy ones
            await AsyncStorage.multiSet([
              [e2eeCompleteKey(userId), String(e2eeComplete)],
              [isNewUserKey(userId), String(isNew)],
            ]);
            await AsyncStorage.multiRemove([LEGACY_E2EE_COMPLETE_KEY, LEGACY_IS_NEW_USER_KEY]);
          }
        }

        // The legacy global-key migration that used to sit here is gone, and
        // deliberately so. Its trigger — "this user has no scoped key" — was
        // also the state of every NEW user on an upgraded device, nothing
        // checked whose key the global record was, and it set hasLocalKey with
        // no seed written. User B could inherit user A's key and be routed past
        // the recovery prompt. There is no unscoped slot to migrate from now.

        // Load persisted personal team ID
        let personalTeamId: string | null = null;
        if (userId) {
          personalTeamId = await AsyncStorage.getItem(personalTeamIdKey(userId)).catch(() => null);
        }

        set({
          e2eeSetupComplete: e2eeComplete,
          isNewUser: isNew,
          hasLocalKey: keyProbe.hasKey,
          keyStorageError: keyProbe.error,
          isInitialized: true,
          currentUserId: userId,
          personalTeamId,
        });
      } catch (error) {
        console.error("Failed to initialize encryption store:", error);
        captureException(error, { context: "encryptionStore.initialize" });
        set({ isInitialized: true });
      }
    },

    reinitializeForUser: async (userId: string) => {
      try {
        const currentUserId = get().currentUserId;
        if (currentUserId === userId) return; // already initialized for this user

        const keyProbe = await probeStoredKey(userId);

        // Load persisted personal team ID for this user
        const personalTeamId = await AsyncStorage.getItem(personalTeamIdKey(userId)).catch(() => null);

        set({
          hasLocalKey: keyProbe.hasKey,
          keyStorageError: keyProbe.error,
          currentUserId: userId,
          personalTeamId,
        });
      } catch (error) {
        console.error("Failed to reinitialize encryption for user:", error);
        captureException(error, { context: "encryptionStore.reinitializeForUser" });
      }
    },

    setE2EEComplete: (complete: boolean) => {
      const userId = get().currentUserId;
      const key = userId ? e2eeCompleteKey(userId) : LEGACY_E2EE_COMPLETE_KEY;
      AsyncStorage.setItem(key, String(complete)).catch((err) => {
        console.error("Failed to persist e2eeSetupComplete:", err);
        captureException(err, { context: "encryptionStore.setE2EEComplete" });
      });
      set({ e2eeSetupComplete: complete });
    },

    setRecoveryPhrase: (phrase: string[]) => set({ recoveryPhrase: phrase }),

    setIsNewUser: (isNew: boolean) => {
      const userId = get().currentUserId;
      const key = userId ? isNewUserKey(userId) : LEGACY_IS_NEW_USER_KEY;
      AsyncStorage.setItem(key, String(isNew)).catch((err) => {
        console.error("Failed to persist isNewUser:", err);
        captureException(err, { context: "encryptionStore.setIsNewUser" });
      });
      set({ isNewUser: isNew });
    },

    setHasLocalKey: (has: boolean) => set({ hasLocalKey: has }),

    clearSensitiveData: () =>
      set({ recoveryPhrase: null }),

    clearSession: () => {
      AsyncStorage.removeItem(LEGACY_PASSKEY_ENROLLMENT_KEY).catch((err) => {
        console.error("Failed to clear passkey enrollment flag:", err);
        captureException(err, { context: "encryptionStore.clearSession.passkey" });
      });
      // Soft logout: clear session-specific state only.
      // Preserve hasLocalKey and e2eeSetupComplete because the encryption
      // key is still on device — clearing them causes a flash of the restore
      // screen on re-login before reinitializeForUser discovers the key.
      get().clearTeamKeys();
      set({
        recoveryPhrase: null,
        isNewUser: false,
            isInitialized: true, // keep true — state is known (cleared), no async reload needed
        currentUserId: null,
      });
    },

    forgetDevice: async (fallbackUserId?: string) => {
      // `currentUserId` is null after any clearSession, and a null id used to
      // send the deletes below to an unscoped slot nobody writes to — "forget
      // this device" reported success and left the key exactly where it was.
      const userId = get().currentUserId ?? fallbackUserId ?? null;
      if (!userId) {
        const error = new Error(
          "forgetDevice ran with no user id; key material was NOT removed from this device"
        );
        console.error(error.message);
        captureException(error, { context: "encryptionStore.forgetDevice" });
      } else {
        try {
          await clearStoredKey(userId);
          await clearSeed(userId);
        } catch (error) {
          console.error("Failed to clear encryption key:", error);
          captureException(error, { context: "encryptionStore.forgetDevice" });
        }
      }
      // Remove both user-scoped and legacy keys to ensure a clean slate. The
      // persisted personal team id goes too — it is device state for this
      // user, and "clean slate" was not true while it stayed behind.
      const keysToRemove = [LEGACY_PASSKEY_ENROLLMENT_KEY, LEGACY_E2EE_COMPLETE_KEY, LEGACY_IS_NEW_USER_KEY];
      if (userId) {
        keysToRemove.push(e2eeCompleteKey(userId), isNewUserKey(userId), personalTeamIdKey(userId));
      }
      AsyncStorage.multiRemove(keysToRemove).catch(
        (err) => {
          console.error("Failed to clear encryption storage:", err);
          captureException(err, { context: "encryptionStore.forgetDevice.storage" });
        }
      );
      set({
        e2eeSetupComplete: false,
        recoveryPhrase: null,
        isNewUser: false,
        hasLocalKey: false,
        keyStorageError: null,
            isInitialized: true, // keep true — state is known (cleared), no async reload needed
        currentUserId: null,
        personalTeamId: null,
        teamKeys: {},
      });
    },

    setPersonalTeamId: (teamId) => {
      set({ personalTeamId: teamId });
      const userId = get().currentUserId;
      if (userId) {
        AsyncStorage.setItem(personalTeamIdKey(userId), teamId).catch(() => {});
      }
    },
    setTeamKey: (teamId, key) => {
      set((state) => ({
        teamKeys: { ...state.teamKeys, [teamId]: key },
      }));
    },
    getTeamKey: (teamId) => {
      return get().teamKeys[teamId] ?? null;
    },
    clearTeamKeys: () => {
      set({ teamKeys: {}, personalTeamId: null });
    },
  })
);
